// Словари и мелкие расчёты раздела «Проекты координатора». Без React — чтобы их можно
// было проверить без отрисовки.
//
// Расшифровки стадий и пунктов чек-листа приходят из ядра (refs), а здесь лежит только
// то, что до ядра не касается: как показать состояние, если расшифровки не пришло, и
// как одинаково посчитать «сколько закрыто». Одинаково — ключевое слово: доля закрытых
// пунктов считается и в плитке, и в строке таблицы, и разойтись они не должны.

/** Название по коду. Незнакомый код показываем кодом, а не подменяем похожим. */
export function поКоду(словарь: Record<string, string> | undefined,
                       код: string | null | undefined,
                       пусто = '—'): string {
  if (!код) return пусто
  return (словарь ?? {})[код] ?? код
}

/**
 * Доля закрытых пунктов чек-листа, 0..100.
 *
 * Возвращает null, а не ноль, когда пунктов нет вовсе: «0 из 0» и «0 из 13» — разные
 * вещи, и рисовать их одинаковой пустой полосой значило бы сказать, что работа не
 * начата, там где её и не заводили.
 */
export function долязакрытых(закрыто: number | null | undefined,
                             всего: number | null | undefined): number | null {
  const в = всего ?? 0
  if (в <= 0) return null
  const з = Math.max(0, Math.min(закрыто ?? 0, в))
  return Math.round((з / в) * 100)
}

/**
 * Состояние карточки одним словом — то, ради чего раздел открывают.
 *
 * Порядок проверок важен: «пустая» сильнее всего остального. Карточка заводится сама
 * при первом открытии экрана проекта на брифинге, и карточка без единой отметки — это
 * не «проект в подготовке», а след чужого клика. Показать её как подготовку значило бы
 * пересчитать чужие клики в работу.
 */
export type Состояние = 'empty' | 'stale' | 'moving' | 'done'

export function состояние(строка: {
  filled?: boolean | null
  stage?: string | null
  last_event_at?: string | null
}, сегодня: Date = new Date()): Состояние {
  if (!строка.filled) return 'empty'
  if (строка.stage === 'done') return 'done'
  const т = строка.last_event_at ? Date.parse(строка.last_event_at) : NaN
  if (!Number.isNaN(т)) {
    const дней = (сегодня.getTime() - т) / 86_400_000
    if (дней > ПОРОГ_ЗАСТОЯ_ДНЕЙ) return 'stale'
  }
  return 'moving'
}

/**
 * Через сколько дней без записей карточка считается застоявшейся.
 *
 * Порог наш, а не источника: на брифинге такого понятия нет вовсе. Он выбран равным
 * двум неделям, потому что чек-лист проекта проходят шагами в несколько дней, и
 * недельная тишина ещё ничего не значит. Число видно здесь одним местом — чтобы его
 * меняли осознанно, а не подгоняли по вкусу в трёх местах экрана.
 */
export const ПОРОГ_ЗАСТОЯ_ДНЕЙ = 14

export const СОСТОЯНИЯ: Record<Состояние, string> = {
  empty: 'пустая',
  stale: 'без движения',
  moving: 'движется',
  done: 'завершён',
}

/** Класс значка состояния. Имена классов — из общих стилей, своих цветов не заводим. */
export const КЛАСС_СОСТОЯНИЯ: Record<Состояние, string> = {
  empty: 'badge',
  stale: 'badge lose',
  moving: 'badge work',
  done: 'badge won',
}
